// frontend/src/components/staff/TimesheetReview.js
//
// Workforce — PAY-005 Admin Timesheet Review screen.
//
// Route: /staff/payroll/timesheets (admin-only).
//
// Authoritative inputs (Sprint 1 retro action item 6 contract-snapshot
// convention — every field rendered cites EXACT keys from these sources):
//   - Design spec: docs/design/PAY-005-timesheet-review-design.md (Zuri,
//     1162 lines, 21 sections, landed 2026-05-02)
//   - Backend contract: docs/architecture/PAY-001-pay-period-engine-tech.md
//     §3.1 entity + §4 captured-curl JSON snapshots (Marcus)
//   - Workforce design system: docs/design/_workforce-design-system.md
//   - Reference patterns:
//       components/staff/DailyReviewDashboard.js (polling + stale-banner)
//       components/staff/StaffProfileForm.js (modal + validation conventions)
//
// Period lifecycle (5 states, forward-only per Marcus PAY-001 §3.2):
//   Open → Closed → Reviewed → Approved → Exported
// PAY-005 owns the Closed→Reviewed and Reviewed→Approved transitions (plus
// the §6.5a Reviewed→Closed reverse affordance). PAY-001 owns Open→Closed.
// PAY-007 (Sprint 3) owns Approved→Exported.
//
// Row lifecycle (within Closed-state period only):
//   pending → approved | rejected (re-decidable until period transitions out
//   of Closed; on Closed→Reviewed all rows lock).
//
// Critical contract notes:
//   - GET /pay-periods/:id/timelogs response does NOT include staffName.
//     Drawer joins client-side via staffProfileId against parent's
//     staffSummaries[]. Marcus deliberate choice to dodge the populate-
//     shape-drift trap (TIME-009 hotfix root cause).
//   - totalRoundedMinutes is null-tolerant per row + per staffSummary.
//     Fallback to rawTotalMinutes; surface "*" annotation per spec §4.2.
//   - notes_legacyProxyIP boolean surfaces an informational chip when true
//     (AUDIT-001 cross-cluster annotation; first Sprint 2 close likely true).
//   - PAY-005-owned PUT endpoints (§9.3.1 transition, §9.3.2 row status) are
//     not in the live backend yet; Simone authors in PAY-005 backend lane.
//     UI ships against published contract; failures surface as toasts.
//
// Sprint 2 deferrals (per spec §11):
//   - Edit-in-place TimeLog correction
//   - Reopen-period flow (terminal at Approved in v1)
//   - Bulk approve/reject; per-staff bulk
//   - Pagination (5-10 staff studio scale; deferred per §13)
//
// PAY-007 (Sprint 3) — CSV export wired in this file:
//   - Approved state: Export CSV button enabled; click triggers
//     GET /api/workforce/pay-periods/:id/export/csv, browser download,
//     and a refetch (status flips Approved → Exported server-side).
//   - Exported state: Re-export CSV button enabled (audit-traceable;
//     original exportedAt/exportedBy preserved server-side).
//   - Closed/Reviewed states: button stays disabled with the user-facing
//     "Export will be available once the pay period is approved." tooltip.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { payPeriodAPI } from "../../services/api";
import { useAuth } from "../AuthContext";
import { formatStudioTime } from "../../utils/workforceTimeUtils";
import { isAdminRole } from "../../utils/workforceUtils";
import StaffLayout from "./StaffLayout";
// TIME-010 — admin direct-edit + change-history surfaces.
import TimeLogEditModal from "./TimeLogEditModal";
import ChangeHistoryDrawer from "./ChangeHistoryDrawer";
import "./TimesheetReview.css";

const POLL_INTERVAL_MS = 30_000;
const STALE_BANNER_THRESHOLD_MS = 2 * 60 * 1000;

const PERIOD_STATUS = {
  OPEN: "Open",
  CLOSED: "Closed",
  REVIEWED: "Reviewed",
  APPROVED: "Approved",
  EXPORTED: "Exported",
};

const ROW_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
};

const REJECT_REASON_MIN_LENGTH = 10;
const REJECT_REASON_MAX_LENGTH = 280;

// ---------- Pure helpers (testable) ----------

/**
 * Format integer cents as USD ($X,XXX.XX). Handles null/undefined as "—".
 * @param {number|null|undefined} cents
 * @returns {string}
 */
export const formatCents = (cents) => {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return "—";
  const dollars = cents / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Format integer cents as strict USD ($X,XXX.XX) — never returns "—".
 * Used by MT rows where zero-revenue ($0.00) is a legitimate value (e.g. 100%
 * promo discount) and must visually render as $0.00 rather than an em-dash.
 * Per Zuri spec §11 Visual DoD condition #14 ("Currency formatting strict")
 * and PAY-MT-001-C AC Scenario 8 (zero-revenue MT row).
 * @param {number|null|undefined} cents
 * @returns {string}
 */
export const formatCentsStrict = (cents) => {
  const safe = typeof cents === "number" && !Number.isNaN(cents) ? cents : 0;
  const dollars = safe / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Format an ISO timestamp as Phoenix wall-clock "MMM D, YYYY h:mm A MST".
 * Phoenix has no DST (fixed UTC-7 year-round), so we shift by 7h and read
 * the resulting Date in UTC for display. Per CLAUDE.md timezone rule + Zuri
 * spec §6 + PAY-MT-001-C AC Scenario 10.
 * @param {string|Date|null|undefined} iso
 * @returns {string}
 */
export const formatPhoenixDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const PHX_OFFSET_MS = 7 * 60 * 60 * 1000;
  const phx = new Date(d.getTime() - PHX_OFFSET_MS);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const month = months[phx.getUTCMonth()];
  const day = phx.getUTCDate();
  const year = phx.getUTCFullYear();
  let hours = phx.getUTCHours();
  const mins = phx.getUTCMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const minStr = String(mins).padStart(2, "0");
  return `${month} ${day}, ${year} ${hours}:${minStr} ${ampm} MST`;
};

/**
 * Shape-tolerant MT row normaliser. Mirrors the `aggregatedRowCounts`
 * defensive pattern: when the BE has not yet shipped Story A's `mtSummaries[]`
 * field, the page degrades to pre-feature visual state. This helper takes a
 * raw rollup entry (Marcus §2 / Zuri §2 shape) and returns a normalised
 * representation safe for rendering, with cents-integer math preserved.
 *
 * Naming note: backend ships `mtSummaries[]` per Marcus §2; Zuri spec also
 * references `mtRows`. Both names tolerated here for forward-compat.
 *
 * @param {object|null|undefined} raw
 * @returns {{
 *   key: string,
 *   providerName: string,
 *   sessionsCompleted: number,
 *   grossRevenueCents: number,
 *   mtShareCents: number,
 *   daShareCents: number,
 *   sessionIds: Array<string>,
 *   sessions: Array<object>|null,
 *   varFlag: boolean,
 *   varReasons: Array<string>,
 * }|null}
 */
export const normaliseMtRow = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  // Cents fields are the canonical contract per Marcus §2; tolerate dollar
  // fallbacks (Zuri §2 referenced `grossRevenue` as USD) by upcasting to cents.
  const grossRevenueCents =
    typeof raw.grossRevenueCents === "number"
      ? raw.grossRevenueCents
      : typeof raw.grossRevenue === "number"
      ? Math.round(raw.grossRevenue * 100)
      : 0;
  const mtShareCents =
    typeof raw.mtShareCents === "number"
      ? raw.mtShareCents
      : typeof raw.mtShare === "number"
      ? Math.round(raw.mtShare * 100)
      : Math.floor(grossRevenueCents * 0.6);
  const daShareCents =
    typeof raw.daShareCents === "number"
      ? raw.daShareCents
      : typeof raw.daShare === "number"
      ? Math.round(raw.daShare * 100)
      : grossRevenueCents - mtShareCents;
  const providerId = raw.providerId || raw.mtId || raw._id || "";
  const providerName = raw.providerName || raw.mtName || "";
  const sessionsCompleted =
    typeof raw.sessionsCompleted === "number" ? raw.sessionsCompleted : 0;
  // sync drill-down: if the BE ships `sessions[]` (Zuri §2 optional), use it;
  // otherwise sessionIds[] is the audit-trail handle and the drill-down shows
  // a "session detail unavailable" defensive message (Zuri §4.3 empty case).
  const sessions = Array.isArray(raw.sessions) ? raw.sessions : null;
  const sessionIds = Array.isArray(raw.sessionIds) ? raw.sessionIds : [];
  // varFlag: BE may populate; FE-compute fallback per PAY-MT-001-C AC note —
  // surface column shape but do not auto-trigger v1 unless BE flags it.
  const varFlag = raw.varFlag === true;
  const varReasons = Array.isArray(raw.varReasons) ? raw.varReasons : [];
  return {
    key: String(providerId || providerName || Math.random()),
    providerName,
    sessionsCompleted,
    grossRevenueCents,
    mtShareCents,
    daShareCents,
    sessionIds,
    sessions,
    varFlag,
    varReasons,
  };
};

/**
 * Normalise an entire MT-rollup array. Returns `null` when the source field
 * is absent (graceful degradation per AC Scenario 3 — pre-Story-A periods do
 * NOT render the MT section header). Returns `[]` (empty array) when the
 * field is present but empty — empty-state placeholder renders.
 *
 * Accepts either `mtSummaries` (Marcus §2 canonical) or `mtRows` (Zuri §2 alt)
 * per shape-tolerance precedent.
 *
 * @param {object|null|undefined} payPeriod
 * @returns {Array<object>|null}
 */
export const selectMtRows = (payPeriod) => {
  if (!payPeriod || typeof payPeriod !== "object") return null;
  const raw =
    Array.isArray(payPeriod.mtSummaries)
      ? payPeriod.mtSummaries
      : Array.isArray(payPeriod.mtRows)
      ? payPeriod.mtRows
      : null;
  if (raw === null) return null;
  const normalised = raw
    .map(normaliseMtRow)
    .filter((r) => r !== null)
    .sort((a, b) => a.providerName.localeCompare(b.providerName));
  return normalised;
};

/**
 * Format integer minutes as "Xh.Ym" rounded display ("38.5h" form). Returns
 * "—*" if minutes is null (spec §4.2 null-rounded fallback marker).
 * @param {number|null|undefined} minutes
 * @returns {string}
 */
export const formatHoursDecimal = (minutes) => {
  if (minutes === null || minutes === undefined) return "—*";
  const hours = (minutes / 60).toFixed(1);
  return `${hours}h`;
};

/**
 * Format integer minutes as "XhYYm" raw display ("38h27m" form). Returns
 * "—" for null/undefined.
 * @param {number|null|undefined} minutes
 * @returns {string}
 */
export const formatHoursRaw = (minutes) => {
  if (minutes === null || minutes === undefined) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h${String(m).padStart(2, "0")}m`;
};

/**
 * Format a period's date range as "M/D – M/D, YYYY" (Phoenix-tz). Reads
 * the periodStart/periodEnd ISO strings (UTC; Phoenix midnight semantics
 * per Marcus PAY-001 §4.1).
 * @param {string|Date} periodStart
 * @param {string|Date} periodEnd
 * @returns {string}
 */
export const formatPeriodRange = (periodStart, periodEnd) => {
  if (!periodStart || !periodEnd) return "";
  // periodStart is Phoenix midnight start-of-day expressed in UTC; render
  // the Phoenix calendar date (NOT the UTC date). Phoenix is fixed UTC-7
  // year-round (no DST) so subtracting 7 hours yields a JS Date whose UTC
  // parts match the Phoenix wall-clock calendar parts.
  const PHX_OFFSET_MS = 7 * 60 * 60 * 1000;
  const phxStart = new Date(new Date(periodStart).getTime() - PHX_OFFSET_MS);
  const phxEnd = new Date(new Date(periodEnd).getTime() - PHX_OFFSET_MS);
  const sm = phxStart.getUTCMonth() + 1;
  const sd = phxStart.getUTCDate();
  const em = phxEnd.getUTCMonth() + 1;
  const ed = phxEnd.getUTCDate();
  const ey = phxEnd.getUTCFullYear();
  return `${sm}/${sd} – ${em}/${ed}, ${ey}`;
};

/**
 * Format a picker option label: "M/D – M/D, YYYY (Status)" for a pay-period
 * row from `payPeriodAPI.list()`. Defensive against missing status field — falls
 * back to the date range alone if status is absent. WORKFORCE-PAY-PERIOD-PICKER-001.
 * @param {{periodStart:string, periodEnd:string, status?:string}|null|undefined} period
 * @returns {string}
 */
export const formatPickerOptionLabel = (period) => {
  if (!period || !period.periodStart || !period.periodEnd) return "—";
  const range = formatPeriodRange(period.periodStart, period.periodEnd);
  const status = typeof period.status === "string" ? period.status : "";
  return status ? `${range} (${status})` : range;
};

/**
 * Derive aggregate row counts from a PayPeriod's timelogs list. Used by the
 * level-1 header card (Approved/Pending/Rejected stack).
 * @param {Array<{status:string}>} timelogs
 */
export const computeRowCounts = (timelogs) => {
  const counts = { approved: 0, pending: 0, rejected: 0, total: 0 };
  if (!Array.isArray(timelogs)) return counts;
  for (const t of timelogs) {
    counts.total += 1;
    if (t.status === ROW_STATUS.APPROVED) counts.approved += 1;
    else if (t.status === ROW_STATUS.REJECTED) counts.rejected += 1;
    else if (t.status === ROW_STATUS.PENDING) counts.pending += 1;
  }
  return counts;
};

/**
 * Aggregate-status-pill rule per spec §4.5. Given a staff's per-row statuses,
 * return one of "Pending (N)" | "Approved" | "Rejected" | "Mixed (P:n R:m A:k)".
 * @param {Array<string>} rowStatuses
 */
export const computeAggregateStatus = (rowStatuses) => {
  if (!Array.isArray(rowStatuses) || rowStatuses.length === 0) {
    return { label: "Pending (0)", kind: "pending" };
  }
  const counts = { pending: 0, approved: 0, rejected: 0 };
  rowStatuses.forEach((s) => {
    if (s === ROW_STATUS.PENDING) counts.pending += 1;
    else if (s === ROW_STATUS.APPROVED) counts.approved += 1;
    else if (s === ROW_STATUS.REJECTED) counts.rejected += 1;
  });
  if (counts.pending > 0 && counts.approved === 0 && counts.rejected === 0) {
    return { label: `Pending (${counts.pending})`, kind: "pending" };
  }
  if (counts.approved > 0 && counts.pending === 0 && counts.rejected === 0) {
    return { label: "Approved", kind: "approved" };
  }
  if (counts.rejected > 0 && counts.pending === 0 && counts.approved === 0) {
    return { label: "Rejected", kind: "rejected" };
  }
  return {
    label: `Mixed (P:${counts.pending} R:${counts.rejected} A:${counts.approved})`,
    kind: "mixed",
  };
};

/**
 * Period-status display config — pill label + token class, action visibility.
 * Spec §3.1 + §4.2 action column table.
 */
const PERIOD_STATUS_DISPLAY = {
  [PERIOD_STATUS.OPEN]: {
    pillLabel: "Open — period in progress",
    pillClass: "trv-pill-open",
  },
  [PERIOD_STATUS.CLOSED]: {
    pillLabel: "Closed — review needed",
    pillClass: "trv-pill-closed",
  },
  [PERIOD_STATUS.REVIEWED]: {
    pillLabel: "Reviewed — awaiting approval",
    pillClass: "trv-pill-reviewed",
  },
  [PERIOD_STATUS.APPROVED]: {
    pillLabel: "Approved — locked",
    pillClass: "trv-pill-approved",
  },
  [PERIOD_STATUS.EXPORTED]: {
    pillLabel: "Exported",
    pillClass: "trv-pill-exported",
  },
};

// ---------- Sub-components ----------

const StaleBanner = () => (
  <div className="trv-stale-banner" role="status" data-testid="trv-stale-banner">
    Data may be out of date — having trouble reaching the server. Showing the
    last known state.
  </div>
);

const VarianceBanner = () => (
  <div className="trv-variance-banner" data-testid="trv-variance-banner">
    Auto-clock-out (TIME-007) and missing-punch correction (TIME-010) variance
    markers will surface here when those features ship in Sprint 3+. Geo-fence
    rejection siblings ARE active — flagged with{" "}
    <span aria-hidden="true">⚠</span> on staff rows below.
  </div>
);

const LegacyProxyChip = () => (
  <span
    className="trv-legacy-proxy-chip"
    data-testid="trv-legacy-proxy-chip"
    title={
      "One or more TimeLogs in this period were punched before the trust-proxy fix landed (commit 66ff00d). " +
      "Audit metadata may show the load-balancer IP rather than the originating client IP. " +
      "Wage compliance unaffected; informational only."
    }
  >
    <span aria-hidden="true">ⓘ</span> Legacy proxy IP
  </span>
);

/**
 * Reject-reason modal (spec §6.4). Reused by initial Reject and by Edit-reason.
 * Validates ≥10 chars min, ≤280 chars max, non-empty. No `[X]` close affordance
 * (mandatory-reason convention).
 */
const RejectReasonModal = ({
  open,
  staffName,
  rowDateLabel,
  initialReason,
  onCancel,
  onSubmit,
  submitting,
  errorMessage,
}) => {
  const [reason, setReason] = useState(initialReason || "");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setReason(initialReason || "");
      // Defer focus so the modal mounts first.
      setTimeout(() => {
        if (textareaRef.current) textareaRef.current.focus();
      }, 0);
    }
  }, [open, initialReason]);

  if (!open) return null;
  const trimmedLength = reason.trim().length;
  const tooShort = trimmedLength > 0 && trimmedLength < REJECT_REASON_MIN_LENGTH;
  const valid = trimmedLength >= REJECT_REASON_MIN_LENGTH;

  return (
    <div
      className="trv-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trv-reject-heading"
      data-testid="trv-reject-modal"
    >
      <div className="trv-modal-card">
        <h2 id="trv-reject-heading" className="trv-modal-heading">
          Reject TimeLog row
        </h2>
        <p className="trv-modal-subheading">
          {staffName ? `${staffName} — ${rowDateLabel}` : rowDateLabel}
        </p>
        <label className="trv-modal-label" htmlFor="trv-reject-reason-input">
          Reason for rejection
        </label>
        <textarea
          id="trv-reject-reason-input"
          ref={textareaRef}
          className="trv-modal-textarea"
          value={reason}
          maxLength={REJECT_REASON_MAX_LENGTH}
          rows={4}
          aria-required="true"
          aria-describedby="trv-reject-reason-help"
          placeholder="e.g., 'Forgot to clock out — staff member confirmed the actual time.'"
          onChange={(e) => setReason(e.target.value)}
          data-testid="trv-reject-modal-textarea"
        />
        <div id="trv-reject-reason-help" className="trv-modal-help">
          Required, at least {REJECT_REASON_MIN_LENGTH} characters. {trimmedLength}/{REJECT_REASON_MAX_LENGTH}
        </div>
        {tooShort && (
          <div className="trv-modal-error" role="alert">
            Reason must be at least {REJECT_REASON_MIN_LENGTH} characters
          </div>
        )}
        {errorMessage && (
          <div className="trv-modal-error" role="alert">
            {errorMessage}
          </div>
        )}
        <div className="trv-modal-footer">
          <button
            type="button"
            className="trv-btn trv-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="trv-reject-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="trv-btn trv-btn-primary"
            onClick={() => onSubmit(reason.trim())}
            disabled={!valid || submitting}
            data-testid="trv-reject-modal-submit"
          >
            {submitting ? "Submitting…" : "Reject row"}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Generic confirmation modal — used for Mark Reviewed, Approve Period, and
 * Back-to-Closed flows (spec §6.5, §6.6, §6.5a). The mandatory-reason
 * convention does not apply here (no textarea); these modals expose an [X]
 * close affordance via the Cancel button only (still no top-right glyph per
 * design system §4 — modal exit is via Cancel + submit).
 */
const ConfirmationModal = ({
  open,
  heading,
  body,
  checklist,
  cancelLabel = "Cancel",
  confirmLabel,
  onCancel,
  onConfirm,
  submitting,
  errorMessage,
  testId,
}) => {
  if (!open) return null;
  return (
    <div
      className="trv-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-heading`}
      data-testid={testId}
    >
      <div className="trv-modal-card">
        <h2 id={`${testId}-heading`} className="trv-modal-heading">
          {heading}
        </h2>
        <p className="trv-modal-body">{body}</p>
        {checklist && checklist.length > 0 && (
          <ul className="trv-modal-checklist">
            {checklist.map((item, i) => (
              <li key={i}>
                <span aria-hidden="true">✓</span> {item}
              </li>
            ))}
          </ul>
        )}
        {errorMessage && (
          <div className="trv-modal-error" role="alert">
            {errorMessage}
          </div>
        )}
        <div className="trv-modal-footer">
          <button
            type="button"
            className="trv-btn trv-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="trv-btn trv-btn-primary"
            onClick={onConfirm}
            disabled={submitting}
            data-testid={`${testId}-confirm`}
          >
            {submitting ? "Submitting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Per-row card inside the drawer. Renders punch values, break, hours, pay,
 * and per-row action area state-dependent on row status + period state.
 */
const TimeLogRowCard = ({
  row,
  hourlyRateCents,
  periodLocked,
  rowSubmittingId,
  onApprove,
  onReject,
  onUndo,
  onReApprove,
  onEditReason,
  onEdit,
  onViewChangeHistory,
}) => {
  const status = row.status || ROW_STATUS.PENDING;
  const inLabel = formatStudioTime(row.punchInTime);
  const outLabel = row.punchOutTime ? formatStudioTime(row.punchOutTime) : "—";
  const minutes =
    row.roundedTotalMinutes !== null && row.roundedTotalMinutes !== undefined
      ? row.roundedTotalMinutes
      : row.rawTotalMinutes;
  const roundedMissing =
    row.roundedTotalMinutes === null || row.roundedTotalMinutes === undefined;
  const payCents =
    typeof minutes === "number" && typeof hourlyRateCents === "number"
      ? Math.trunc((hourlyRateCents * minutes) / 60)
      : null;
  const hoursLabel =
    typeof minutes === "number" ? formatHoursDecimal(minutes) : "—";
  const dateLabel = row.punchInTime
    ? new Date(
        new Date(row.punchInTime).getTime() - 7 * 60 * 60 * 1000
      ).toLocaleDateString("en-US", {
        weekday: "short",
        month: "numeric",
        day: "numeric",
        timeZone: "UTC",
      })
    : "—";

  const submitting = rowSubmittingId === row._id;

  let cardClass = "trv-row-card";
  if (status === ROW_STATUS.APPROVED) cardClass += " trv-row-card-approved";
  if (status === ROW_STATUS.REJECTED) cardClass += " trv-row-card-rejected";

  let pillClass = "trv-row-pill trv-row-pill-pending";
  let pillLabel = "Pending review";
  if (status === ROW_STATUS.APPROVED) {
    pillClass = "trv-row-pill trv-row-pill-approved";
    pillLabel = "Approved";
  } else if (status === ROW_STATUS.REJECTED) {
    pillClass = "trv-row-pill trv-row-pill-rejected";
    pillLabel = "Rejected";
  }

  return (
    <div
      className={cardClass}
      data-testid={`trv-row-card-${row._id}`}
      data-row-status={status}
    >
      <div className="trv-row-card-header">
        <span className="trv-row-card-date">{dateLabel}</span>
        <span className={pillClass}>
          {periodLocked && status !== ROW_STATUS.PENDING && (
            <span aria-hidden="true" className="trv-row-pill-lock">
              🔒
            </span>
          )}
          {pillLabel}
        </span>
      </div>
      <div className="trv-row-card-body">
        <div className="trv-row-card-punch">
          <span className="trv-row-card-punch-label">In:</span> {inLabel}
        </div>
        <div className="trv-row-card-punch">
          <span className="trv-row-card-punch-label">Out:</span> {outLabel}
        </div>
        {typeof row.totalBreakMinutes === "number" && (
          <div className="trv-row-card-break">
            Break: {row.totalBreakMinutes}m
          </div>
        )}
        <div className="trv-row-card-totals">
          <span className="trv-row-card-hours">
            Hours: {hoursLabel}
            {roundedMissing && (
              <span className="trv-row-card-rounding-pending">
                {" "}
                (rounding pending)
              </span>
            )}
          </span>
          <span className="trv-row-card-pay">
            Pay: {formatCents(payCents)}
          </span>
        </div>
        {status === ROW_STATUS.REJECTED && row.rejectionReason && (
          <div className="trv-row-card-rejection-reason">
            <span aria-hidden="true">✕</span> Rejected: {row.rejectionReason}
          </div>
        )}
      </div>
      <div className="trv-row-card-actions">
        {/* TIME-010 §5.2 — [Edit] button (tertiary). Always rendered; disabled
            with tooltip when period is locked per §5.3. Order in cluster:
            [Edit] left → [Approve] middle → [Reject] right per §5.2 reading
            order ("do I need to edit first? then approve or reject"). */}
        {typeof onEdit === "function" && (
          <button
            type="button"
            className="trv-btn trv-btn-tertiary trv-btn-compact"
            onClick={() => onEdit(row)}
            disabled={submitting || periodLocked}
            title={
              periodLocked
                ? "Period is locked. Reopen via PAY-006 (coming soon) to edit this row."
                : undefined
            }
            data-testid={`trv-row-edit-${row._id}`}
          >
            <span aria-hidden="true" className="trv-btn-icon-pencil">
              {/* simple unicode pencil glyph — no icon-font dep */}
              ✎
            </span>{" "}
            Edit
          </button>
        )}
        {/* TIME-010 §6.5 — change-history affordance, visible when the row
            has any manualAdjustments[] entries. */}
        {typeof onViewChangeHistory === "function" &&
          Array.isArray(row.manualAdjustments) &&
          row.manualAdjustments.length > 0 && (
            <button
              type="button"
              className="trv-btn-link"
              onClick={() => onViewChangeHistory(row)}
              data-testid={`trv-row-view-history-${row._id}`}
            >
              View change history ({row.manualAdjustments.length})
            </button>
          )}
        {!periodLocked && status === ROW_STATUS.PENDING && (
          <>
            <button
              type="button"
              className="trv-btn trv-btn-primary trv-btn-compact"
              onClick={() => onApprove(row)}
              disabled={submitting}
              data-testid={`trv-row-approve-${row._id}`}
            >
              {submitting ? "…" : "Approve"}
            </button>
            <button
              type="button"
              className="trv-btn trv-btn-secondary trv-btn-compact"
              onClick={() => onReject(row)}
              disabled={submitting}
              data-testid={`trv-row-reject-${row._id}`}
            >
              Reject
            </button>
          </>
        )}
        {!periodLocked && status === ROW_STATUS.APPROVED && (
          <button
            type="button"
            className="trv-btn-link"
            onClick={() => onUndo(row)}
            disabled={submitting}
            data-testid={`trv-row-undo-${row._id}`}
          >
            Undo
          </button>
        )}
        {!periodLocked && status === ROW_STATUS.REJECTED && (
          <>
            <button
              type="button"
              className="trv-btn trv-btn-outlined-primary trv-btn-compact"
              onClick={() => onReApprove(row)}
              disabled={submitting}
              data-testid={`trv-row-reapprove-${row._id}`}
            >
              Re-approve
            </button>
            <button
              type="button"
              className="trv-btn-link"
              onClick={() => onEditReason(row)}
              disabled={submitting}
              data-testid={`trv-row-edit-reason-${row._id}`}
            >
              Edit reason
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ---------- MT row group (PAY-MT-001-C) ----------

/**
 * MT row group: section divider + rows + drill-down + empty-state + footer.
 *
 * Renders ONLY when `mtRows` is a non-null array (graceful degradation per
 * AC Scenario 3 — when `mtSummaries` field is absent on the response, callers
 * pass `null` here and the entire group suppresses).
 *
 * Visual rhythm matches the existing hourly Front Desk table:
 *   - Same `.trv-staff-table` container shape (semantic <table>)
 *   - Reuses `.trv-staff-row` (hover, focus-visible) for hourly-parity
 *   - 60% Share emphasised with `.trv-mt-row-share` (gold, semibold)
 *   - Drill-down expands beneath parent row (Zuri §4.2/§4.3)
 *
 * Per Zuri §4.3 sync vs async drill-down (Q-A): v1 ships SYNC against the
 * `sessions[]` (when present) OR `sessionIds[]` (defensive empty case).
 * Per Zuri §1 rule 3: NO DA share column on the row; DA share lives in CSV
 * (Story B) only.
 *
 * @param {{mtRows: Array<object>|null, periodLocked: boolean}} props
 */
const MtRowGroup = ({ mtRows, periodLocked }) => {
  // Hooks must run unconditionally to satisfy rules-of-hooks; the conditional
  // null-return below uses the hook result without re-ordering.
  const [expandedKey, setExpandedKey] = useState(null);
  const [varReasonAnchor, setVarReasonAnchor] = useState(null);

  const toggleExpand = useCallback(
    (key, options = {}) => {
      setExpandedKey((prev) => (prev === key ? null : key));
      if (options.scrollToVarReasons) {
        setVarReasonAnchor(key);
      } else {
        setVarReasonAnchor(null);
      }
    },
    []
  );

  // Pre-Story-A graceful degradation: missing field, render nothing.
  if (mtRows === null) return null;

  const isEmpty = mtRows.length === 0;

  // MT Total Share footer value (suppressed in empty-state per Zuri §4.5).
  const mtTotalShareCents = mtRows.reduce(
    (acc, r) => acc + (typeof r.mtShareCents === "number" ? r.mtShareCents : 0),
    0
  );

  return (
    <section
      className="trv-mt-section"
      data-testid="trv-mt-section"
      aria-labelledby="trv-mt-section-heading"
    >
      <hr className="trv-mt-section-divider" aria-hidden="true" />
      <h3
        id="trv-mt-section-heading"
        className="trv-mt-section-heading"
        data-testid="trv-mt-section-heading"
      >
        MASSAGE THERAPISTS
      </h3>

      {isEmpty ? (
        <div
          className="trv-mt-empty"
          role="status"
          data-testid="trv-mt-empty"
        >
          <p className="trv-mt-empty-primary">
            No MT sessions completed this pay period
          </p>
          <p className="trv-mt-empty-secondary">
            The Massage Therapist section will populate when a completed
            session is recorded against an MT.
          </p>
        </div>
      ) : (
        <table
          className="trv-staff-table trv-mt-table"
          data-testid="trv-mt-table"
        >
          <thead>
            <tr>
              <th scope="col">Staff</th>
              <th scope="col">Role</th>
              <th scope="col">Sessions</th>
              <th scope="col">Gross Revenue</th>
              <th scope="col">60% Share</th>
              <th scope="col" aria-label="Variance">
                Var
              </th>
              <th scope="col" aria-label="Drill-in"></th>
            </tr>
          </thead>
          <tbody>
            {mtRows.map((r) => {
              const isExpanded = expandedKey === r.key;
              const drillDownId = `trv-mt-drilldown-${r.key}`;
              return (
                <React.Fragment key={r.key}>
                  <tr
                    className="trv-staff-row trv-mt-row"
                    onClick={() => toggleExpand(r.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExpand(r.key);
                      } else if (e.key === "Escape" && isExpanded) {
                        setExpandedKey(null);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={isExpanded}
                    aria-controls={drillDownId}
                    aria-label={`Toggle session detail for ${r.providerName}`}
                    data-testid={`trv-mt-row-${r.key}`}
                  >
                    <td className="trv-staff-row-name trv-mt-row-name">
                      {r.providerName}
                    </td>
                    <td className="trv-staff-row-role trv-mt-row-role">
                      Massage Therapist
                    </td>
                    <td className="trv-staff-row-mono trv-mt-row-sessions">
                      {r.sessionsCompleted}
                    </td>
                    <td className="trv-staff-row-mono trv-mt-row-gross">
                      {formatCentsStrict(r.grossRevenueCents)}
                    </td>
                    <td className="trv-staff-row-mono trv-mt-row-share">
                      {formatCentsStrict(r.mtShareCents)}
                    </td>
                    <td className="trv-staff-row-variance">
                      {r.varFlag ? (
                        <button
                          type="button"
                          className="trv-variance-icon trv-mt-var-icon"
                          aria-label="Anomaly flagged for this row"
                          title={
                            r.varReasons.length > 0
                              ? r.varReasons.join("; ")
                              : "Anomaly flagged — see drill-down"
                          }
                          data-testid={`trv-mt-var-${r.key}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(r.key, {
                              scrollToVarReasons: true,
                            });
                          }}
                        >
                          <span aria-hidden="true">⚠</span>
                        </button>
                      ) : (
                        ""
                      )}
                    </td>
                    <td
                      className="trv-staff-row-chevron trv-mt-row-chevron"
                      aria-hidden="true"
                    >
                      {isExpanded ? "▾" : "▸"}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr
                      className="trv-mt-drilldown-row"
                      data-testid={`trv-mt-drilldown-${r.key}`}
                    >
                      <td
                        colSpan={7}
                        id={drillDownId}
                        className="trv-mt-drilldown-cell"
                      >
                        <MtDrillDown
                          row={r}
                          highlightVarReasons={varReasonAnchor === r.key}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr
              className="trv-mt-footer-row"
              data-testid="trv-mt-footer-row"
            >
              <td className="trv-mt-footer-spacer" colSpan={4}></td>
              <td className="trv-mt-footer-label">MT Total Share</td>
              <td className="trv-mt-footer-value">
                {formatCentsStrict(mtTotalShareCents)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      )}

      {periodLocked && !isEmpty && (
        <p className="trv-mt-section-footnote">
          Period closed — MT data is read-only. Drill-down still available for
          audit.
        </p>
      )}
    </section>
  );
};

/**
 * Drill-down per-MT panel. Renders a per-session table when `sessions[]` is
 * present on the rollup, or a defensive "session detail unavailable" message
 * when only `sessionIds[]` is present (Zuri §4.3 empty case).
 *
 * Var-reasons block surfaces at top when `varFlag === true`, with `role="alert"`
 * only when explicitly anchored (Zuri §9 — no over-announcement on every
 * re-render).
 */
const MtDrillDown = ({ row, highlightVarReasons }) => {
  const sessions = Array.isArray(row.sessions) ? row.sessions : null;
  const subtotalGrossCents =
    sessions !== null
      ? sessions.reduce((acc, s) => {
          const c =
            typeof s.sessionPriceCents === "number"
              ? s.sessionPriceCents
              : typeof s.sessionPrice === "number"
              ? Math.round(s.sessionPrice * 100)
              : 0;
          return acc + c;
        }, 0)
      : 0;
  const subtotalMtShareCents =
    sessions !== null
      ? sessions.reduce((acc, s) => {
          const c =
            typeof s.mtShareForSessionCents === "number"
              ? s.mtShareForSessionCents
              : typeof s.mtShareForSession === "number"
              ? Math.round(s.mtShareForSession * 100)
              : 0;
          return acc + c;
        }, 0)
      : 0;

  return (
    <div className="trv-mt-drilldown-content">
      {row.varFlag && (
        <div
          className="trv-mt-var-banner"
          role={highlightVarReasons ? "alert" : undefined}
          data-testid="trv-mt-var-banner"
        >
          <p className="trv-mt-var-banner-heading">Anomaly flagged</p>
          {row.varReasons.length > 0 ? (
            <ul className="trv-mt-var-reasons">
              {row.varReasons.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p className="trv-mt-var-reasons-empty">
              No additional detail available.
            </p>
          )}
        </div>
      )}

      {sessions === null ? (
        <p
          className="trv-mt-drilldown-defensive"
          data-testid="trv-mt-drilldown-defensive"
        >
          {row.sessionsCompleted > 0
            ? "Session detail unavailable for this period."
            : "No sessions to display."}
        </p>
      ) : sessions.length === 0 ? (
        <p
          className="trv-mt-drilldown-defensive"
          data-testid="trv-mt-drilldown-empty"
        >
          Session detail unavailable for this period.
        </p>
      ) : (
        <table
          className="trv-mt-drilldown-table"
          data-testid="trv-mt-drilldown-table"
        >
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Customer</th>
              <th scope="col">Session Price</th>
              <th scope="col">MT Share</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s, i) => {
              const sessionId =
                s.sessionId || s._id || `${row.key}-${i}`;
              const priceCents =
                typeof s.sessionPriceCents === "number"
                  ? s.sessionPriceCents
                  : typeof s.sessionPrice === "number"
                  ? Math.round(s.sessionPrice * 100)
                  : 0;
              const shareCents =
                typeof s.mtShareForSessionCents === "number"
                  ? s.mtShareForSessionCents
                  : typeof s.mtShareForSession === "number"
                  ? Math.round(s.mtShareForSession * 100)
                  : 0;
              return (
                <tr
                  key={sessionId}
                  className="trv-mt-drilldown-session-row"
                  tabIndex={0}
                  data-testid={`trv-mt-drilldown-session-${sessionId}`}
                >
                  <td className="trv-mt-drilldown-date">
                    {formatPhoenixDateTime(s.completedAt || s.appointmentDate)}
                  </td>
                  <td className="trv-mt-drilldown-customer">
                    {s.customerName || "—"}
                  </td>
                  <td className="trv-mt-drilldown-mono">
                    {formatCentsStrict(priceCents)}
                  </td>
                  <td className="trv-mt-drilldown-mono trv-mt-drilldown-share">
                    {formatCentsStrict(shareCents)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr
              className="trv-mt-drilldown-subtotal"
              data-testid="trv-mt-drilldown-subtotal"
            >
              <td colSpan={2} className="trv-mt-drilldown-subtotal-label">
                Subtotal
              </td>
              <td className="trv-mt-drilldown-mono">
                {formatCentsStrict(subtotalGrossCents)}
              </td>
              <td className="trv-mt-drilldown-mono trv-mt-drilldown-share">
                {formatCentsStrict(subtotalMtShareCents)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

// ---------- Main component ----------

const TimesheetReview = () => {
  const { user } = useAuth();
  const adminAccess = isAdminRole(user?.role);

  // WORKFORCE-PAY-PERIOD-PICKER-001 — URL deep-link via ?periodId=<id>.
  // useSearchParams MUST be called unconditionally before any early return
  // (rules-of-hooks). On mount we read ?periodId and pre-select; on change we
  // setSearchParams({ periodId }, { replace: true }) so back-button history
  // doesn't pile up.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPeriodIdFromUrl = searchParams.get("periodId") || null;

  // Period summary state
  const [payPeriod, setPayPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollError, setPollError] = useState(false);
  const [pollErrorSince, setPollErrorSince] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [errorToast, setErrorToast] = useState(null);

  // WORKFORCE-PAY-PERIOD-PICKER-001 — period picker state.
  //   periodList: result of payPeriodAPI.list() — null = not-yet-loaded;
  //                [] = empty result; [...] = populated.
  //   selectedPeriodId: which period is currently driving the data fetch.
  //                null = current (default; preserves regression behavior).
  //   listLoadFailed: true if list endpoint returned 404 (BE not deployed yet).
  //                When true, picker collapses to "Current Period" sole entry
  //                (Leg 4 graceful fallback).
  const [periodList, setPeriodList] = useState(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState(
    initialPeriodIdFromUrl
  );
  // eslint-disable-next-line no-unused-vars -- state driven by API 404 (setListLoadFailed at 1302/1309); consumer render-conditional pending WORKFORCE-PAY-PERIOD-PICKER-001 Leg 4 followup
  const [listLoadFailed, setListLoadFailed] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Drawer state
  const [drawerStaff, setDrawerStaff] = useState(null); // staffSummary object
  const [drawerTimelogs, setDrawerTimelogs] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Modal state
  const [rejectModal, setRejectModal] = useState({
    open: false,
    row: null,
    initialReason: "",
    error: null,
    submitting: false,
  });
  const [transitionModal, setTransitionModal] = useState({
    open: false,
    to: null,
    submitting: false,
    error: null,
  });

  // Per-row in-flight tracker (one at a time per spec §7.1 + §6.1)
  const [rowSubmittingId, setRowSubmittingId] = useState(null);

  const intervalRef = useRef(null);

  // ---------- Data fetch ----------

  // WORKFORCE-PAY-PERIOD-PICKER-001 — generalized fetcher.
  // When `selectedPeriodId` is null → calls payPeriodAPI.getCurrent() (preserves
  // regression behavior for staff who don't interact with the picker).
  // When `selectedPeriodId` is set → calls payPeriodAPI.getById(id) — works
  // against any past period the BE list endpoint surfaced. Response envelope
  // is { payPeriod } in both cases per Marcus PAY-001 §4.2.
  const fetchCurrent = useCallback(
    async (silent = false) => {
      if (!adminAccess) return;
      try {
        const response = selectedPeriodId
          ? await payPeriodAPI.getById(selectedPeriodId)
          : await payPeriodAPI.getCurrent();
        // §4.2 contract — `payPeriod` envelope.
        const envelope = response?.data;
        const period = envelope && envelope.payPeriod;
        if (!period) {
          throw new Error("Malformed response — missing payPeriod envelope");
        }
        setPayPeriod(period);
        setLastUpdatedAt(new Date());
        setPollError(false);
        setPollErrorSince(null);
      } catch (err) {
        if (!silent) {
          // Initial load error: surface but keep page renderable
          setErrorToast({
            kind: "error",
            message: "Couldn't reach the server. Try again in a moment.",
          });
        }
        setPollError(true);
        setPollErrorSince((prev) => prev || new Date());
      } finally {
        setLoading(false);
      }
    },
    [adminAccess, selectedPeriodId]
  );

  // WORKFORCE-PAY-PERIOD-PICKER-001 — mount fetch of period list.
  // Runs once per admin session. On 404 we set listLoadFailed=true and the
  // picker collapses to "Current Period" sole entry (BE-not-deployed-yet
  // graceful fallback). On any other error we silently fall through to the
  // same single-entry state — fetchCurrent() still drives the page data so
  // staff workflow is preserved.
  useEffect(() => {
    if (!adminAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await payPeriodAPI.list({ status: "all", limit: 12 });
        if (cancelled) return;
        const envelope = response?.data;
        const list =
          envelope && Array.isArray(envelope.payPeriods)
            ? envelope.payPeriods
            : [];
        setPeriodList(list);
        setListLoadFailed(false);
      } catch (err) {
        if (cancelled) return;
        // 404 (BE not deployed) OR any other failure — collapse picker to
        // current-period-only. Do NOT surface a toast: TimesheetReview
        // remains fully functional via the existing getCurrent() path.
        setPeriodList([]);
        setListLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminAccess]);

  // Mount + polling
  useEffect(() => {
    if (!adminAccess) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        if (!cancelled) fetchCurrent(true);
      }, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        fetchCurrent(true);
        startPolling();
      } else {
        stopPolling();
      }
    };

    fetchCurrent(false);
    startPolling();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    return () => {
      cancelled = true;
      stopPolling();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [adminAccess, fetchCurrent]);

  // WORKFORCE-PAY-PERIOD-PICKER-001 — picker change handler.
  // When a staff selects a different period: update URL (replace, not push,
  // so the back button doesn't pile up; AC Scenario 4 mandate), update local
  // state. The data fetch is driven by the selectedPeriodId-dependent
  // fetchCurrent useCallback above — changing selectedPeriodId triggers a
  // re-run of the mount+polling useEffect because fetchCurrent's identity
  // changes.
  const handlePeriodChange = useCallback(
    (rawId) => {
      // Empty string from the <select> ("Current Period" option) → reset to
      // null which collapses back to getCurrent() behavior.
      const nextId = rawId || null;
      setSelectedPeriodId(nextId);
      const next = new URLSearchParams(searchParams);
      if (nextId) {
        next.set("periodId", nextId);
      } else {
        next.delete("periodId");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // WORKFORCE-PAY-PERIOD-PICKER-001 — picker dropdown options.
  // Composition rules:
  //   - Always include a "Current Period" sentinel option (value="") at the
  //     top — selecting it resets back to getCurrent() and clears ?periodId.
  //   - Followed by every period from the list endpoint (most recent first;
  //     BE sorts descending by periodStart per the contract).
  //   - When listLoadFailed (BE not deployed yet) or periodList is empty: only
  //     the "Current Period" entry shows.
  //   - Defensive: deduplicate if list contains the current period (it may);
  //     the sentinel already covers it.
  const pickerOptions = useMemo(() => {
    const list = Array.isArray(periodList) ? periodList : [];
    return list.map((p) => ({
      id: p._id,
      label: formatPickerOptionLabel(p),
      status: p.status,
    }));
  }, [periodList]);

  // ---------- Drawer ----------

  const openDrawer = useCallback(
    async (staffSummary) => {
      if (!payPeriod || !payPeriod._id) return;
      setDrawerStaff(staffSummary);
      setDrawerLoading(true);
      try {
        const response = await payPeriodAPI.getTimeLogs(payPeriod._id, {
          staffProfileId: staffSummary.staffProfileId,
        });
        const list =
          (response?.data && Array.isArray(response.data.timelogs)
            ? response.data.timelogs
            : []) || [];
        setDrawerTimelogs(list);
      } catch (err) {
        setDrawerTimelogs([]);
        setErrorToast({
          kind: "error",
          message: "Couldn't load timesheet rows. Try again in a moment.",
        });
      } finally {
        setDrawerLoading(false);
      }
    },
    [payPeriod]
  );

  const closeDrawer = useCallback(() => {
    setDrawerStaff(null);
    setDrawerTimelogs([]);
  }, []);

  // Esc closes drawer
  useEffect(() => {
    if (!drawerStaff) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerStaff, closeDrawer]);

  const refreshDrawerTimelogs = useCallback(async () => {
    if (!payPeriod || !payPeriod._id || !drawerStaff) return;
    try {
      const response = await payPeriodAPI.getTimeLogs(payPeriod._id, {
        staffProfileId: drawerStaff.staffProfileId,
      });
      const list =
        (response?.data && Array.isArray(response.data.timelogs)
          ? response.data.timelogs
          : []) || [];
      setDrawerTimelogs(list);
    } catch (err) {
      // Silent — keep prior rows on screen
    }
  }, [payPeriod, drawerStaff]);

  // ---------- Per-row actions ----------

  const handleRowApprove = useCallback(
    async (row) => {
      setRowSubmittingId(row._id);
      try {
        await payPeriodAPI.updateTimeLogStatus(row._id, {
          status: ROW_STATUS.APPROVED,
        });
        // Optimistic local update + background re-fetch for canonical state.
        setDrawerTimelogs((prev) =>
          prev.map((t) =>
            t._id === row._id ? { ...t, status: ROW_STATUS.APPROVED } : t
          )
        );
        // Refresh period rollup so header counts re-render canonical.
        fetchCurrent(true);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 409) {
          setErrorToast({
            kind: "warn",
            message:
              "This row was just updated by another admin. Refreshing…",
          });
          refreshDrawerTimelogs();
          fetchCurrent(true);
        } else if (status === 403) {
          setErrorToast({
            kind: "error",
            message: "You don't have permission to do that.",
          });
        } else {
          setErrorToast({
            kind: "error",
            message:
              "Couldn't reach the server. Try again in a moment.",
          });
        }
      } finally {
        setRowSubmittingId(null);
      }
    },
    [fetchCurrent, refreshDrawerTimelogs]
  );

  const handleRowReject = useCallback((row) => {
    setRejectModal({
      open: true,
      row,
      initialReason: row.rejectionReason || "",
      error: null,
      submitting: false,
    });
  }, []);

  const handleRowEditReason = useCallback((row) => {
    setRejectModal({
      open: true,
      row,
      initialReason: row.rejectionReason || "",
      error: null,
      submitting: false,
    });
  }, []);

  // TIME-010 §5 — admin direct-edit modal + change-history drawer state.
  const [editModalState, setEditModalState] = useState({ open: false, row: null });
  const [changeHistoryState, setChangeHistoryState] = useState({
    open: false,
    timeLog: null,
  });

  const handleRowEdit = useCallback((row) => {
    setEditModalState({ open: true, row });
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditModalState({ open: false, row: null });
  }, []);

  const handleEditSaved = useCallback(
    (updatedTimeLog) => {
      // Replace the row in drawerTimelogs with the server's authoritative
      // post-edit shape. Marcus tech doc §4.5 returns the full TimeLog
      // including the new manualAdjustments[] entry at the last index.
      setDrawerTimelogs((prev) =>
        prev.map((r) => (r._id === updatedTimeLog._id ? updatedTimeLog : r))
      );
      setEditModalState({ open: false, row: null });
      setErrorToast({
        kind: "success",
        message: "Changes saved. Audit log updated.",
      });
    },
    []
  );

  const handleViewChangeHistory = useCallback((row) => {
    setChangeHistoryState({ open: true, timeLog: row });
  }, []);

  const handleCloseChangeHistory = useCallback(() => {
    setChangeHistoryState({ open: false, timeLog: null });
  }, []);

  const handleRejectModalSubmit = useCallback(
    async (reason) => {
      const row = rejectModal.row;
      if (!row) return;
      setRejectModal((prev) => ({ ...prev, submitting: true, error: null }));
      try {
        await payPeriodAPI.updateTimeLogStatus(row._id, {
          status: ROW_STATUS.REJECTED,
          rejectionReason: reason,
        });
        setDrawerTimelogs((prev) =>
          prev.map((t) =>
            t._id === row._id
              ? { ...t, status: ROW_STATUS.REJECTED, rejectionReason: reason }
              : t
          )
        );
        setRejectModal({
          open: false,
          row: null,
          initialReason: "",
          error: null,
          submitting: false,
        });
        fetchCurrent(true);
      } catch (err) {
        const status = err?.response?.status;
        let message = "Couldn't reach the server. Try again in a moment.";
        if (status === 409) {
          message = "This row was just updated by another admin.";
        } else if (status === 403) {
          message = "You don't have permission to do that.";
        }
        setRejectModal((prev) => ({
          ...prev,
          submitting: false,
          error: message,
        }));
      }
    },
    [rejectModal.row, fetchCurrent]
  );

  const handleRejectModalCancel = useCallback(() => {
    setRejectModal({
      open: false,
      row: null,
      initialReason: "",
      error: null,
      submitting: false,
    });
  }, []);

  const handleRowUndo = useCallback(
    async (row) => {
      setRowSubmittingId(row._id);
      try {
        await payPeriodAPI.updateTimeLogStatus(row._id, {
          status: ROW_STATUS.PENDING,
        });
        setDrawerTimelogs((prev) =>
          prev.map((t) =>
            t._id === row._id
              ? { ...t, status: ROW_STATUS.PENDING, rejectionReason: undefined }
              : t
          )
        );
        fetchCurrent(true);
      } catch (err) {
        setErrorToast({
          kind: "error",
          message: "Couldn't reach the server. Try again in a moment.",
        });
      } finally {
        setRowSubmittingId(null);
      }
    },
    [fetchCurrent]
  );

  const handleRowReApprove = useCallback(
    async (row) => {
      // Reuses approve endpoint
      await handleRowApprove(row);
      // Clear the rejection reason on the local row
      setDrawerTimelogs((prev) =>
        prev.map((t) =>
          t._id === row._id ? { ...t, rejectionReason: undefined } : t
        )
      );
    },
    [handleRowApprove]
  );

  // ---------- Period transitions ----------

  const openTransitionModal = useCallback((to) => {
    setTransitionModal({ open: true, to, submitting: false, error: null });
  }, []);

  const closeTransitionModal = useCallback(() => {
    setTransitionModal({ open: false, to: null, submitting: false, error: null });
  }, []);

  const handleTransitionConfirm = useCallback(async () => {
    if (!payPeriod || !transitionModal.to) return;
    setTransitionModal((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      // Open→Closed is the materialization step — no PayPeriod document exists
      // yet (synthetic view, _id === null). Use POST /close to materialize.
      // All other transitions operate on a persisted document via PUT /transition.
      const isOpenToClosed =
        payPeriod.status === PERIOD_STATUS.OPEN &&
        transitionModal.to === PERIOD_STATUS.CLOSED;
      if (isOpenToClosed) {
        // Backend requires asOfDate explicitly; the tech-doc "defaults to
        // today" claim isn't honored by the controller (logged for Marcus
        // doc/impl reconciliation 2026-05-02).
        await payPeriodAPI.close({ asOfDate: new Date().toISOString() });
      } else {
        if (!payPeriod._id) return;
        await payPeriodAPI.transition(payPeriod._id, transitionModal.to);
      }
      closeTransitionModal();
      fetchCurrent(true);
    } catch (err) {
      const status = err?.response?.status;
      let message = "Couldn't reach the server. Try again in a moment.";
      if (status === 422) {
        message =
          err?.response?.data?.error ||
          "Preconditions for this transition are not met.";
      } else if (status === 409) {
        message = "Period was just updated by another admin.";
      } else if (status === 403) {
        message = "You don't have permission to do that.";
      }
      setTransitionModal((prev) => ({ ...prev, submitting: false, error: message }));
    }
  }, [payPeriod, transitionModal.to, closeTransitionModal, fetchCurrent]);

  // ---------- PAY-007 — CSV export ----------
  //
  // Triggers GET /api/workforce/pay-periods/:id/export/csv (admin-only,
  // gated server-side to status `Approved` or `Exported`). Receives a Blob,
  // synthesizes an anchor-tag click to drive the browser download, then
  // refetches the period so the UI picks up the server-side `Approved` →
  // `Exported` flip and surfaces the new audit-strip metadata.
  //
  // Filename strategy: prefer the BE-provided `Content-Disposition` filename
  // when CORS exposes it; fall back to a deterministic client-built name
  // matching the BE format `payroll_export_<code>_<start>_<end>_csv.csv`
  // (see backend/utils/payrollCsvExport.js buildExportFilename). Today
  // backend index.js exposedHeaders does not include `Content-Disposition`
  // for the web origin, so the fallback is the live path; flagged to
  // Simone for a follow-up CORS exposure update.
  const [exportingCsv, setExportingCsv] = useState(false);

  const handleExportCsv = useCallback(async () => {
    if (!payPeriod || !payPeriod._id) return;
    setExportingCsv(true);
    try {
      const response = await payPeriodAPI.exportCsv(payPeriod._id);
      const blob = response?.data;
      if (!blob) {
        throw new Error("Empty export response");
      }

      // Filename — prefer header, fall back to client-built name.
      let filename = null;
      const contentDisposition =
        response?.headers?.["content-disposition"] ||
        response?.headers?.["Content-Disposition"];
      if (contentDisposition) {
        const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
        if (match && match[1]) filename = match[1];
      }
      if (!filename) {
        const code = payPeriod?.studioLocationCode || "UNKNOWN";
        const fmtYmd = (iso) => {
          if (!iso) return "00000000";
          const d = new Date(iso);
          if (Number.isNaN(d.getTime())) return "00000000";
          const y = d.getUTCFullYear();
          const m = String(d.getUTCMonth() + 1).padStart(2, "0");
          const day = String(d.getUTCDate()).padStart(2, "0");
          return `${y}${m}${day}`;
        };
        filename = `payroll_export_${code}_${fmtYmd(
          payPeriod.periodStart
        )}_${fmtYmd(payPeriod.periodEnd)}_csv.csv`;
      }

      // Trigger browser download. URL.createObjectURL + anchor click is the
      // CRA-compatible path; matches the pattern other admin-export flows
      // use. revokeObjectURL on next tick to free the blob.
      const url = window.URL.createObjectURL(
        blob instanceof Blob ? blob : new Blob([blob], { type: "text/csv" })
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => window.URL.revokeObjectURL(url), 0);

      // Server-side this flips Approved → Exported on first export. Refetch
      // so the audit strip reflects the new state.
      fetchCurrent(true);
    } catch (err) {
      // Note: blob responseType means error bodies arrive as Blobs too. We
      // surface a generic toast — the BE error envelope is JSON inside the
      // blob and decoding it for a user-facing message isn't worth the
      // complexity at this scale (admin-only surface, retry is cheap).
      const status = err?.response?.status;
      let message = "Couldn't export the pay period. Try again in a moment.";
      if (status === 403) {
        message = "You don't have permission to export this pay period.";
      } else if (status === 422) {
        message =
          "Pay period must be approved before it can be exported.";
      } else if (status === 404) {
        message = "Pay period not found.";
      }
      setErrorToast({ kind: "error", message });
    } finally {
      setExportingCsv(false);
    }
  }, [payPeriod, fetchCurrent]);

  // ---------- Derived state ----------

  const periodStatus = payPeriod?.status || PERIOD_STATUS.OPEN;
  const periodIsLocked =
    periodStatus === PERIOD_STATUS.REVIEWED ||
    periodStatus === PERIOD_STATUS.APPROVED ||
    periodStatus === PERIOD_STATUS.EXPORTED;
  const periodIsPersisted = !!(payPeriod && payPeriod._id);

  // For Sprint 2 the contract does NOT surface aggregated row-status counts on
  // the PayPeriod document. Approved/Pending/Rejected counts in the header
  // card derive from the persisted-period's TimeLogs. To avoid a heavyweight
  // fetch-all-timelogs on the level-1 page we use a defensive shape: if the
  // server adds an `aggregatedRowCounts` field in a future iteration we
  // surface it; otherwise we render dashes when no drawer-fetched data is
  // present. This is documented in the design spec §7.1 ("either optimistic
  // or refetch-driven — see §9.1") — we choose refetch-driven for canonical.
  const headerRowCounts = useMemo(() => {
    // Pull from a shape-tolerant `aggregatedRowCounts` field if present.
    if (payPeriod && payPeriod.aggregatedRowCounts) {
      const a = payPeriod.aggregatedRowCounts;
      return {
        approved: a.approved || 0,
        pending: a.pending || 0,
        rejected: a.rejected || 0,
        total: (a.approved || 0) + (a.pending || 0) + (a.rejected || 0),
      };
    }
    return null;
  }, [payPeriod]);

  // staffSummary aggregate-status pill — derived from timelogs only when the
  // drawer is open (we don't have per-staff row statuses on the rollup).
  // For the level-1 staff table we render a "—" placeholder for the pill
  // until the drawer fetch surfaces row-level status. Spec §4.5 uses this
  // pill, but the underlying contract field is not present in the rollup;
  // graceful degradation noted in §9.4 divergence-handling.
  const drawerRowStatusList = useMemo(
    () => drawerTimelogs.map((t) => t.status),
    [drawerTimelogs]
  );
  const drawerAggregateStatus = computeAggregateStatus(drawerRowStatusList);
  const drawerCounts = computeRowCounts(drawerTimelogs);

  const filteredStaff = useMemo(() => {
    if (!payPeriod || !Array.isArray(payPeriod.staffSummaries)) return [];
    const needle = searchTerm.trim().toLowerCase();
    return payPeriod.staffSummaries.filter((s) => {
      if (!s) return false;
      if (needle) {
        const haystack = [s.staffName, s.primaryRole]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      // Status filter is best-effort — without per-staff row statuses on the
      // rollup we treat "all" as the default and the others as a no-op
      // unless the server adds aggregateStatus per staff. Documented in
      // spec §4.4 / §9.4 divergence-handling.
      void statusFilter;
      return true;
    });
  }, [payPeriod, searchTerm, statusFilter]);

  // Stale banner derived from poll-error duration
  const showStaleBanner =
    pollError &&
    pollErrorSince &&
    Date.now() - pollErrorSince.getTime() >= STALE_BANNER_THRESHOLD_MS;

  // PAY-MT-001-C — Massage Therapist rollup. `selectMtRows` returns:
  //   - null when `mtSummaries` field is absent (pre-Story-A periods → MT
  //     section suppressed entirely; Scenario 3 graceful degradation)
  //   - [] when field is present but empty (empty-state placeholder renders;
  //     Scenario 2)
  //   - [...] sorted ASC by providerName (mixed period; Scenario 1)
  //
  // MUST be called before any early return (Hooks rules-of-hooks).
  const mtRows = useMemo(() => selectMtRows(payPeriod), [payPeriod]);

  // ---------- RBAC defense-in-depth ----------
  if (!adminAccess) {
    return (
      <StaffLayout>
        <div className="trv-container">
          <h1 className="trv-page-title">Workforce — Timesheet Review</h1>
          <p className="trv-rbac-warning" role="alert">
            You don't have permission to view payroll timesheets. Contact a
            studio administrator if you need access.
          </p>
        </div>
      </StaffLayout>
    );
  }

  // ---------- Render ----------

  const periodDisplay =
    PERIOD_STATUS_DISPLAY[periodStatus] || PERIOD_STATUS_DISPLAY.Open;
  const periodRange = payPeriod
    ? formatPeriodRange(payPeriod.periodStart, payPeriod.periodEnd)
    : "";
  const totalRoundedMinutesArr = (payPeriod?.staffSummaries || []).map(
    (s) => s.totalRoundedMinutes
  );
  const anyNullRounded = totalRoundedMinutesArr.some(
    (v) => v === null || v === undefined
  );
  const nullRoundedCount = totalRoundedMinutesArr.filter(
    (v) => v === null || v === undefined
  ).length;
  const totalRoundedMinutesSum = totalRoundedMinutesArr.reduce(
    (acc, v) => acc + (typeof v === "number" ? v : 0),
    0
  );
  const totalRawMinutesSum = (payPeriod?.staffSummaries || []).reduce(
    (acc, s) => acc + (typeof s.totalRawMinutes === "number" ? s.totalRawMinutes : 0),
    0
  );
  const displayedHoursMinutes = anyNullRounded
    ? totalRawMinutesSum
    : totalRoundedMinutesSum;
  const totalGrossPayCents = (payPeriod?.staffSummaries || []).reduce(
    (acc, s) =>
      acc + (typeof s.totalGrossPayCents === "number" ? s.totalGrossPayCents : 0),
    0
  );
  const staffCount = (payPeriod?.staffSummaries || []).length;

  // Loading skeleton on first paint
  if (loading && !payPeriod) {
    return (
      <StaffLayout>
        <div className="trv-container">
          <p className="trv-breadcrumb">
            Workforce &gt; Payroll &gt; Timesheets
          </p>
          <div className="trv-skeleton-card" data-testid="trv-loading">
            Loading timesheet review…
          </div>
        </div>
      </StaffLayout>
    );
  }

  return (
    <StaffLayout>
      <div className="trv-container" data-testid="trv-root">
        <p className="trv-breadcrumb">
          Workforce &gt; Payroll &gt; Timesheets
        </p>
        <header className="trv-header">
          <div>
            <h1 className="trv-page-title">
              Pay Period{periodRange ? `: ${periodRange}` : ""}
            </h1>
          </div>
          {/* WORKFORCE-PAY-PERIOD-PICKER-001 — period picker dropdown.
              Default selection is "Current Period" (empty value) — preserves
              regression behavior for staff who don't interact with the picker
              (AC Scenario 1). On change → fetchCurrent re-fires via the
              selectedPeriodId-dependent useCallback. When the BE list endpoint
              hasn't deployed yet (listLoadFailed) or returns empty (Scenario
              5), only the "Current Period" sentinel option renders. */}
          <div
            className="trv-period-picker"
            data-testid="trv-period-picker"
          >
            <label
              htmlFor="trv-period-picker-select"
              className="trv-period-picker-label"
            >
              Period:
            </label>
            <select
              id="trv-period-picker-select"
              className="trv-period-picker-select"
              data-testid="trv-period-picker-select"
              value={selectedPeriodId || ""}
              onChange={(e) => handlePeriodChange(e.target.value)}
            >
              <option value="">Current Period</option>
              {pickerOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div
            className="trv-refresh-chip"
            data-testid="trv-refresh-chip"
            aria-live="polite"
          >
            <span className="trv-refresh-label">
              Last updated{" "}
              {lastUpdatedAt ? formatStudioTime(lastUpdatedAt) : "—"}
            </span>
            <button
              type="button"
              className="trv-refresh-button"
              onClick={() => fetchCurrent(true)}
              aria-label="Refresh now"
              data-testid="trv-refresh-button"
            >
              Refresh
            </button>
          </div>
        </header>

        {/* Period header card */}
        <section
          className="trv-period-card"
          data-testid="trv-period-card"
          data-period-status={periodStatus}
        >
          <div className="trv-period-card-top">
            <span
              className={`trv-pill ${periodDisplay.pillClass}`}
              data-testid="trv-period-pill"
            >
              {periodDisplay.pillLabel}
            </span>
            {payPeriod && payPeriod.notes_legacyProxyIP === true && (
              <LegacyProxyChip />
            )}
          </div>
          <div className="trv-period-card-summary">
            <span className="trv-period-card-range">{periodRange}</span>
            <span className="trv-period-card-sep">·</span>
            <span className="trv-period-card-staff-count">
              {staffCount} staff
            </span>
            <span className="trv-period-card-sep">·</span>
            <span className="trv-period-card-hours">
              {formatHoursDecimal(displayedHoursMinutes)}
              {anyNullRounded ? "*" : ""} {anyNullRounded ? "raw" : "rounded"}
            </span>
            <span className="trv-period-card-sep">·</span>
            <span className="trv-period-card-pay">
              {formatCents(totalGrossPayCents)}
            </span>
          </div>
          {anyNullRounded && (
            <div className="trv-period-card-footnote">
              * {nullRoundedCount} staff missing rounding (TIME-008 not yet
              shipped)
            </div>
          )}

          <div className="trv-period-card-actions-row">
            <div className="trv-period-card-counts">
              {headerRowCounts ? (
                <>
                  <div className="trv-period-card-count trv-period-card-count-approved">
                    Approved: {headerRowCounts.approved} / {headerRowCounts.total}{" "}
                    rows
                  </div>
                  <div className="trv-period-card-count trv-period-card-count-pending">
                    Pending: {headerRowCounts.pending}
                  </div>
                  {headerRowCounts.rejected > 0 && (
                    <div className="trv-period-card-count trv-period-card-count-rejected">
                      Rejected: {headerRowCounts.rejected}
                    </div>
                  )}
                </>
              ) : (
                <div className="trv-period-card-count-placeholder">
                  Open a staff drill-in to review per-row totals.
                </div>
              )}
            </div>
            <div className="trv-period-card-actions">
              {periodStatus === PERIOD_STATUS.OPEN && (
                <>
                  <button
                    type="button"
                    className="trv-btn trv-btn-secondary"
                    onClick={() => openTransitionModal(PERIOD_STATUS.CLOSED)}
                    data-testid="trv-close-period-early-btn"
                    title="Materialize the pay-period and unlock review actions. Use only when ending the period early; otherwise wait for the natural cycle boundary."
                  >
                    Close Period Early
                  </button>
                  <span className="trv-period-card-banner">
                    Period is in progress. Review activates after the period
                    closes.
                  </span>
                </>
              )}
              {periodStatus === PERIOD_STATUS.CLOSED && (
                <>
                  <button
                    type="button"
                    className="trv-btn trv-btn-primary"
                    onClick={() => openTransitionModal(PERIOD_STATUS.REVIEWED)}
                    disabled={
                      headerRowCounts ? headerRowCounts.pending > 0 : false
                    }
                    title={
                      headerRowCounts && headerRowCounts.pending > 0
                        ? `${headerRowCounts.pending} rows still pending review.`
                        : ""
                    }
                    data-testid="trv-mark-reviewed-btn"
                  >
                    Mark Reviewed
                  </button>
                  <button
                    type="button"
                    className="trv-btn trv-btn-secondary"
                    disabled
                    title="Export will be available once the pay period is approved."
                    data-testid="trv-export-csv-btn"
                  >
                    Export CSV
                  </button>
                </>
              )}
              {periodStatus === PERIOD_STATUS.REVIEWED && (
                <>
                  <button
                    type="button"
                    className="trv-btn trv-btn-primary"
                    onClick={() => openTransitionModal(PERIOD_STATUS.APPROVED)}
                    data-testid="trv-approve-period-btn"
                  >
                    Approve Period
                  </button>
                  <button
                    type="button"
                    className="trv-btn-link"
                    onClick={() => openTransitionModal(PERIOD_STATUS.CLOSED)}
                    data-testid="trv-back-to-closed-btn"
                  >
                    Back to Closed
                  </button>
                  <button
                    type="button"
                    className="trv-btn trv-btn-secondary"
                    disabled
                    title="Export will be available once the pay period is approved."
                    data-testid="trv-export-csv-btn"
                  >
                    Export CSV
                  </button>
                </>
              )}
              {periodStatus === PERIOD_STATUS.APPROVED && (
                <>
                  <span className="trv-period-card-audit-strip">
                    Approved by {payPeriod?.approvedBy || "—"} at{" "}
                    {payPeriod?.approvedAt
                      ? formatStudioTime(payPeriod.approvedAt)
                      : "—"}
                  </span>
                  <button
                    type="button"
                    className="trv-btn trv-btn-primary"
                    onClick={handleExportCsv}
                    disabled={exportingCsv}
                    title="Download CSV"
                    data-testid="trv-export-csv-btn"
                  >
                    {exportingCsv ? "Exporting…" : "Export CSV"}
                  </button>
                </>
              )}
              {periodStatus === PERIOD_STATUS.EXPORTED && (
                <>
                  <span className="trv-period-card-audit-strip">
                    Exported by {payPeriod?.exportedBy || "—"} at{" "}
                    {payPeriod?.exportedAt
                      ? formatStudioTime(payPeriod.exportedAt)
                      : "—"}
                  </span>
                  <button
                    type="button"
                    className="trv-btn trv-btn-secondary"
                    onClick={handleExportCsv}
                    disabled={exportingCsv}
                    title="Re-download CSV. Original export timestamp is preserved."
                    data-testid="trv-export-csv-btn"
                  >
                    {exportingCsv ? "Exporting…" : "Re-export CSV"}
                  </button>
                </>
              )}
            </div>
          </div>
        </section>

        {showStaleBanner && <StaleBanner />}

        <VarianceBanner />

        {/* Filter row */}
        <div className="trv-filters">
          <input
            type="search"
            className="trv-search"
            placeholder="Search staff..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search staff"
            data-testid="trv-search"
          />
          <label className="trv-filter-label">
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
              data-testid="trv-status-filter"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
        </div>

        {/* Staff rollup table */}
        {filteredStaff.length === 0 ? (
          <div className="trv-empty" role="status" data-testid="trv-empty">
            <p className="trv-empty-headline">
              {staffCount === 0
                ? "No staff have clocked in for this period yet."
                : "No staff match the current filters."}
            </p>
            {staffCount === 0 && (
              <p className="trv-empty-detail">
                When the first TimeLog is recorded for this period, it will
                appear here.
              </p>
            )}
          </div>
        ) : (
          <table className="trv-staff-table" data-testid="trv-staff-table">
            <thead>
              <tr>
                <th scope="col">Staff</th>
                <th scope="col">Role</th>
                <th scope="col">Rounded hours</th>
                <th scope="col">Raw hours</th>
                <th scope="col">Gross pay</th>
                <th scope="col" aria-label="Variance">
                  Var
                </th>
                <th scope="col" aria-label="Drill-in"></th>
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map((s) => {
                const roundedNull =
                  s.totalRoundedMinutes === null ||
                  s.totalRoundedMinutes === undefined;
                const variance =
                  s.variance && s.variance.hasGeofenceRejectionSibling;
                return (
                  <tr
                    key={s.staffProfileId}
                    className={
                      periodIsPersisted
                        ? "trv-staff-row"
                        : "trv-staff-row trv-staff-row-disabled"
                    }
                    onClick={
                      periodIsPersisted ? () => openDrawer(s) : undefined
                    }
                    onKeyDown={
                      periodIsPersisted
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openDrawer(s);
                            }
                          }
                        : undefined
                    }
                    tabIndex={periodIsPersisted ? 0 : -1}
                    role={periodIsPersisted ? "button" : undefined}
                    aria-label={
                      periodIsPersisted
                        ? `Open drill-in for ${s.staffName}`
                        : `Drill-in available after the period is closed (${s.staffName})`
                    }
                    title={
                      periodIsPersisted
                        ? undefined
                        : "Drill-in available after the period closes."
                    }
                    data-testid={`trv-staff-row-${s.staffProfileId}`}
                  >
                    <td className="trv-staff-row-name">{s.staffName}</td>
                    <td className="trv-staff-row-role">{s.primaryRole}</td>
                    <td className="trv-staff-row-mono">
                      {roundedNull
                        ? "—*"
                        : formatHoursDecimal(s.totalRoundedMinutes)}
                    </td>
                    <td className="trv-staff-row-mono">
                      {formatHoursRaw(s.totalRawMinutes)}
                    </td>
                    <td className="trv-staff-row-mono trv-staff-row-pay">
                      {formatCents(s.totalGrossPayCents)}
                    </td>
                    <td className="trv-staff-row-variance">
                      {variance ? (
                        <span
                          className="trv-variance-icon"
                          title="Geo-fence rejection sibling detected — staff was rejected from clock-in within this period."
                          aria-label="Geofence rejection sibling"
                        >
                          ⚠
                        </span>
                      ) : (
                        ""
                      )}
                    </td>
                    <td className="trv-staff-row-chevron" aria-hidden="true">
                      ▸
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {anyNullRounded && (
          <p className="trv-table-footnote">
            * rounding pending (TIME-008)
          </p>
        )}

        {/* PAY-MT-001-C — Massage Therapist row group. Conditional render
            via mtRows; null = pre-Story-A graceful degradation, [] = empty
            state placeholder, populated = section + rows + footer. */}
        <MtRowGroup mtRows={mtRows} periodLocked={periodIsLocked} />

        <p className="trv-page-footnote">
          Times shown in MST. Polling every 30 seconds.{" "}
          {periodIsPersisted
            ? "Period drill-down loads on click."
            : "Open period — review activates after the period closes."}
        </p>

        {/* Drill-in drawer */}
        {drawerStaff && (
          <>
            <div
              className="trv-drawer-backdrop"
              onClick={closeDrawer}
              data-testid="trv-drawer-backdrop"
              aria-hidden="true"
            />
            <aside
              className="trv-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="trv-drawer-heading"
              data-testid="trv-drawer"
            >
              <header className="trv-drawer-header">
                <div>
                  <h2 id="trv-drawer-heading" className="trv-drawer-name">
                    {drawerStaff.staffName}
                  </h2>
                  <p className="trv-drawer-subline">
                    {drawerStaff.primaryRole}
                    {periodRange ? ` · ${periodRange}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  className="trv-drawer-close"
                  onClick={closeDrawer}
                  aria-label="Close drawer"
                  data-testid="trv-drawer-close"
                >
                  ×
                </button>
              </header>
              <div className="trv-drawer-summary" aria-live="polite">
                <div className="trv-drawer-summary-line">
                  <span className="trv-drawer-summary-label">Rounded:</span>{" "}
                  <span className="trv-drawer-summary-value">
                    {formatHoursDecimal(drawerStaff.totalRoundedMinutes)}
                  </span>
                </div>
                <div className="trv-drawer-summary-line">
                  <span className="trv-drawer-summary-label">Raw:</span>{" "}
                  <span className="trv-drawer-summary-value-secondary">
                    {formatHoursRaw(drawerStaff.totalRawMinutes)}
                  </span>
                </div>
                <div className="trv-drawer-summary-line">
                  <span className="trv-drawer-summary-label">Gross pay:</span>{" "}
                  <span className="trv-drawer-summary-pay">
                    {formatCents(drawerStaff.totalGrossPayCents)}
                  </span>
                </div>
                <div className="trv-drawer-summary-line">
                  <span className="trv-drawer-summary-label">Status:</span>{" "}
                  <span
                    className={`trv-row-pill trv-row-pill-${drawerAggregateStatus.kind}`}
                  >
                    {drawerAggregateStatus.label}
                  </span>
                </div>
              </div>
              <div className="trv-drawer-body">
                {drawerLoading ? (
                  <p className="trv-drawer-loading" data-testid="trv-drawer-loading">
                    Loading timesheet rows…
                  </p>
                ) : drawerTimelogs.length === 0 ? (
                  <p className="trv-drawer-empty" data-testid="trv-drawer-empty">
                    No TimeLog rows for this staff in this period.
                  </p>
                ) : (
                  <>
                    <p className="trv-drawer-row-count">
                      TimeLog rows ({drawerCounts.total})
                    </p>
                    {drawerTimelogs.map((row) => (
                      <TimeLogRowCard
                        key={row._id}
                        row={row}
                        hourlyRateCents={drawerStaff.hourlyRateCents}
                        periodLocked={periodIsLocked}
                        rowSubmittingId={rowSubmittingId}
                        onApprove={handleRowApprove}
                        onReject={handleRowReject}
                        onUndo={handleRowUndo}
                        onReApprove={handleRowReApprove}
                        onEditReason={handleRowEditReason}
                        onEdit={handleRowEdit}
                        onViewChangeHistory={handleViewChangeHistory}
                      />
                    ))}
                  </>
                )}
              </div>
            </aside>
          </>
        )}

        {/* Reject reason modal */}
        <RejectReasonModal
          open={rejectModal.open}
          staffName={drawerStaff?.staffName}
          rowDateLabel={
            rejectModal.row?.punchInTime
              ? new Date(
                  new Date(rejectModal.row.punchInTime).getTime() -
                    7 * 60 * 60 * 1000
                ).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "numeric",
                  day: "numeric",
                  timeZone: "UTC",
                })
              : ""
          }
          initialReason={rejectModal.initialReason}
          submitting={rejectModal.submitting}
          errorMessage={rejectModal.error}
          onCancel={handleRejectModalCancel}
          onSubmit={handleRejectModalSubmit}
        />

        {/* Period transition modals */}
        <ConfirmationModal
          open={
            transitionModal.open && transitionModal.to === PERIOD_STATUS.REVIEWED
          }
          heading={`Mark period ${periodRange} as Reviewed?`}
          body={
            "Marking the period reviewed locks all per-row decisions. After this transition, individual TimeLog rows cannot be re-Approved or re-Rejected from the review screen. The next step is final approval, which authorizes the period for export."
          }
          checklist={[
            headerRowCounts
              ? `All ${headerRowCounts.total} rows reviewed (0 Pending)`
              : "All rows reviewed",
            headerRowCounts
              ? `${headerRowCounts.approved} approved · ${headerRowCounts.rejected} rejected`
              : null,
          ].filter(Boolean)}
          confirmLabel="Mark Reviewed"
          submitting={transitionModal.submitting}
          errorMessage={transitionModal.error}
          onCancel={closeTransitionModal}
          onConfirm={handleTransitionConfirm}
          testId="trv-mark-reviewed-modal"
        />

        <ConfirmationModal
          open={
            transitionModal.open && transitionModal.to === PERIOD_STATUS.APPROVED
          }
          heading={`Approve Pay Period ${periodRange}?`}
          body={
            "Approving the period locks the rollup totals for export. After approval, no further edits — including row decisions — are possible. The period can be exported to CSV when PAY-007 ships in Sprint 3."
          }
          checklist={[
            payPeriod?.reviewedAt
              ? `Period reviewed at ${formatStudioTime(payPeriod.reviewedAt)}`
              : "Period reviewed",
            headerRowCounts
              ? `${headerRowCounts.approved} approved · ${headerRowCounts.rejected} rejected · 0 pending`
              : null,
            `Total payroll: ${formatCents(totalGrossPayCents)}`,
          ].filter(Boolean)}
          confirmLabel="Approve Period"
          submitting={transitionModal.submitting}
          errorMessage={transitionModal.error}
          onCancel={closeTransitionModal}
          onConfirm={handleTransitionConfirm}
          testId="trv-approve-period-modal"
        />

        {/* Open → Closed (forward, materialize period). Distinct copy from the
            Reviewed → Closed reverse below. */}
        <ConfirmationModal
          open={
            transitionModal.open &&
            transitionModal.to === PERIOD_STATUS.CLOSED &&
            payPeriod?.status === PERIOD_STATUS.OPEN
          }
          heading="Close this pay period?"
          body="This materializes the period and unlocks per-row review (Approve / Reject). The period stops accepting new clock-ins for this cycle. You can still revert to Open by deleting the PayPeriod row in the database; reopen via UI is a future story."
          confirmLabel="Close Period"
          submitting={transitionModal.submitting}
          errorMessage={transitionModal.error}
          onCancel={closeTransitionModal}
          onConfirm={handleTransitionConfirm}
          testId="trv-close-period-early-modal"
        />
        {/* Reviewed → Closed (reverse / undo Mark Reviewed). */}
        <ConfirmationModal
          open={
            transitionModal.open &&
            transitionModal.to === PERIOD_STATUS.CLOSED &&
            payPeriod?.status === PERIOD_STATUS.REVIEWED
          }
          heading="Move period back to Closed?"
          body="This re-enables row-level Approve/Reject actions and reverses the Reviewed lock."
          confirmLabel="Yes, back to Closed"
          submitting={transitionModal.submitting}
          errorMessage={transitionModal.error}
          onCancel={closeTransitionModal}
          onConfirm={handleTransitionConfirm}
          testId="trv-back-to-closed-modal"
        />

        {/* Error toast */}
        {errorToast && (
          <div
            className={`trv-toast trv-toast-${errorToast.kind || "error"}`}
            role="alert"
            data-testid="trv-error-toast"
            onClick={() => setErrorToast(null)}
          >
            <span aria-hidden="true">⚠</span> {errorToast.message}
          </div>
        )}

        {/* TIME-010 §5 — Admin direct-edit modal (modal-on-modal stack
            opens centered over the drill-in drawer; the drawer remains
            visible underneath, dimmed by the modal backdrop). */}
        <TimeLogEditModal
          open={editModalState.open}
          row={editModalState.row}
          onCancel={handleEditCancel}
          onSaved={handleEditSaved}
          onPageToast={(toast) => setErrorToast(toast)}
        />

        {/* TIME-010 §6.5 — Change-history drawer (right-side slide-in;
            mirror of PAY-005 drill-in pattern). Reads
            manualAdjustments[] off the TimeLog row directly per Marcus
            tech doc §15. */}
        <ChangeHistoryDrawer
          open={changeHistoryState.open}
          timeLog={changeHistoryState.timeLog}
          isAdmin={adminAccess}
          staffNameById={null}
          onClose={handleCloseChangeHistory}
        />
      </div>
    </StaffLayout>
  );
};

export default TimesheetReview;
